import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, HandHeart, Sparkles, ArrowRight, Quote } from 'lucide-react';
import SeoHelmet from '../components/SeoHelmet';

const impactStats = [
  { icon: HandHeart, num: 'L3C',         label: 'Social Enterprise',         labelClass: '' },
  { icon: Users,     num: 'By',          label: 'Adults with\nSpecial Needs', labelClass: 'whitespace-pre-line' },
  { icon: Sparkles,  num: 'Every',       label: 'Purchase is\nSpecial',       labelClass: 'whitespace-pre-line' },
  { icon: Heart,     num: 'Since',       label: 'A Vision\nin 2023',       labelClass: 'whitespace-pre-line' },
];

const About = () => {
  return (
    <main className="pt-6" data-testid="about-page">
      <SeoHelmet overrides={{
        title: 'Our Story — But God | Wear The Hope with Ben',
        description: "Our story can be summed up in two life-changing words: But God. A journey of faith, autism, and purpose — creating meaningful employment for adults with disabilities.",
      }} />

      {/* 1) HERO — Deep Navy with warm sunlight glow, gold accents */}
      <section className="relative overflow-hidden gradient-navy text-white">
        <div
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 22% 25%, rgba(212,166,58,0.35), transparent 55%), radial-gradient(circle at 82% 78%, rgba(126,151,113,0.25), transparent 55%)',
          }}
        />
        <div className="relative max-w-[1100px] mx-auto px-6 lg:px-10 py-24 md:py-32 text-center">
          <div className="inline-flex items-center gap-2 border border-brand-gold/40 rounded-full px-4 py-1.5 mb-8">
            <Heart className="w-3.5 h-3.5 text-brand-gold" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-brand-gold">Our Story</span>
          </div>
          <h1 className="font-serif-display text-6xl md:text-8xl leading-[1] text-white">
            Our story can be summed up in two words…
            <br />
            <span className="italic text-brand-gold">But God.</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-white/80 max-w-2xl mx-auto italic font-cormorant">
            “You intended to harm me, <span className="text-brand-gold">but God</span> intended it for good.”
            <span className="block mt-2 text-xs uppercase tracking-[0.3em] text-white/60 not-italic">— Genesis 50:20</span>
          </p>
        </div>
      </section>

      {/* 2) STORY — Warm Cream, generous whitespace, elegant serif reading */}
      <section className="py-24 md:py-32 bg-brand-cream">
        <div className="max-w-[820px] mx-auto px-6 lg:px-10 space-y-8 text-brand-charcoal leading-[1.85]">
          <div className="text-[11px] uppercase tracking-[0.3em] text-brand-gold-dark text-center mb-2">A Testimony of Faith</div>
          <h2 className="font-serif-display text-4xl md:text-5xl text-brand-navy text-center leading-tight mb-4">
            When plans change, and grace begins.
          </h2>

          <p className="text-lg">
            Genesis 50:20 says, <em>“You intended to harm me, but God intended it for good.”</em> Those
            words are more than a Bible verse to us — they are the testimony of our lives.
          </p>

          <p>
            Twenty-four years ago, God blessed us with our first child, <strong className="text-brand-navy">Ben</strong>.
            Like every parent, we dreamed about his future, imagined the milestones he would reach, and
            looked forward to the life ahead of him. But when Ben was just two and a half years old, we
            realized our journey would be very different from what we had expected. We later learned he was
            on the autism spectrum.
          </p>

          <p>
            The years that followed were filled with therapies, appointments, unanswered questions, and
            moments when our hearts quietly wondered, <em>“Lord, what will Ben's future look like?”</em> The
            hardest part was never the diagnosis — it was wondering whether the world would ever see the
            gifts God had placed inside our son.
          </p>

          <p className="text-xl font-cormorant italic text-brand-navy text-center py-6">
            Yet through every tear, every disappointment, and every season of uncertainty, God kept
            reminding us of one truth: <span className="text-brand-gold-dark not-italic font-semibold">But God.</span>
          </p>
        </div>
      </section>

      {/* 3) JOSEPH — Deeper Sage with a gentle cream card. Growth, healing, new beginnings. */}
      <section className="py-24 md:py-28 bg-brand-sage-2 relative">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10">
          <div className="bg-brand-cream rounded-3xl p-8 md:p-14 shadow-xl border border-white/40 relative">
            <Quote className="w-10 h-10 text-brand-gold-dark mb-6" />
            <p className="font-serif-display text-2xl md:text-3xl leading-relaxed text-brand-navy">
              Joseph's story became our story.
            </p>
            <p className="mt-4 text-brand-charcoal leading-[1.85]">
              The pit didn't define him. The prison didn't determine his destiny. God used every painful
              season to prepare him for a greater purpose. We began to believe that perhaps God could do
              the same with our family.
            </p>
            <p className="mt-6 text-lg text-brand-charcoal leading-[1.85]">
              Then, in November 2023, <span className="italic text-brand-gold-dark font-semibold">But God</span> intervened
              once again.
            </p>
            <p className="mt-4 text-brand-charcoal leading-[1.85]">
              God connected us with Paul Isac, founder of Wear The Hope, a
              Christian entrepreneur and technologist in India. He shared a God-given vision that
              immediately reminded us of Habakkuk 2:2: <em>“Write the vision and make it plain, so that
              whoever reads it may run with it.”</em> What began as a conversation became a calling, and
              that calling gave birth to <strong className="text-brand-navy">Wear The Hope with Ben</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* 4) THE MISSION — Warm Cream, elegant continuation */}
      <section className="py-24 md:py-32 bg-brand-cream">
        <div className="max-w-[820px] mx-auto px-6 lg:px-10 space-y-8 text-brand-charcoal leading-[1.85]">
          <div className="text-[11px] uppercase tracking-[0.3em] text-brand-gold-dark text-center mb-2">The Mission Today</div>
          <h2 className="font-serif-display text-4xl md:text-5xl text-brand-navy text-center leading-tight mb-4">
            Purpose stitched into every product.
          </h2>

          <p className="text-lg">
            Today, we are an <strong className="text-brand-navy">L3C social enterprise</strong> with a mission to create
            meaningful employment opportunities for adults with autism and other disabilities while sharing
            the hope of Christ through every product we make. Whether it's custom apparel, tumblers, engraved
            gifts, cutting boards, keychains, or personalized keepsakes, every item carries more than
            beautiful craftsmanship — it carries a story of redemption.
          </p>

          <div className="grid sm:grid-cols-3 gap-4 py-6">
            <div className="bg-white rounded-xl p-6 border border-brand-cream-2 text-center">
              <div className="font-serif-display text-3xl text-brand-gold-dark italic">Every product</div>
              <div className="text-sm text-brand-charcoal mt-2">tells a story</div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-brand-cream-2 text-center">
              <div className="font-serif-display text-3xl text-brand-gold-dark italic">Every product</div>
              <div className="text-sm text-brand-charcoal mt-2">carries hope</div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-brand-cream-2 text-center">
              <div className="font-serif-display text-3xl text-brand-gold-dark italic">Every purchase</div>
              <div className="text-sm text-brand-charcoal mt-2">creates meaningful work</div>
            </div>
          </div>

          <p>
            Our prayer is that when people receive one of our products, they won't simply see a gift.
            They'll see the faithfulness of a God who still transforms pain into purpose, disappointment
            into destiny, and broken dreams into beautiful testimonies.
          </p>
        </div>
      </section>

      {/* 5) IMPACT — Deep Forest Sage with quiet metric cards */}
      <section className="py-20 md:py-24 bg-brand-sage-3 text-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="text-[11px] uppercase tracking-[0.3em] text-brand-cream mb-3">Hope in Motion</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-white">The heart behind every stitch.</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {impactStats.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="bg-white/10 backdrop-blur rounded-2xl p-6 text-center border border-white/20">
                  <Icon className="w-6 h-6 text-brand-gold mx-auto mb-3" />
                  <div className="font-serif-display text-4xl text-brand-gold">{s.num}</div>
                  <div className={`text-xs uppercase tracking-[0.2em] text-white/85 mt-2 leading-relaxed ${s.labelClass || ''}`}>{s.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 6) PASTORAL CLOSING — Warm Cream, quiet and personal */}
      <section className="py-24 md:py-32 bg-brand-cream">
        <div className="max-w-[820px] mx-auto px-6 lg:px-10 space-y-8 text-brand-charcoal leading-[1.85]">
          <h2 className="font-serif-display text-4xl md:text-5xl text-brand-navy text-center leading-tight mb-4">
            Maybe your story isn't over yet.
          </h2>

          <p>
            Maybe today you're standing in your own pit. Maybe you're carrying a diagnosis, grieving a loss,
            facing uncertainty, or wondering if God has forgotten you.
          </p>

          <p className="font-cormorant italic text-2xl text-brand-navy text-center py-4">
            Please don't let your current chapter convince you that your story is over.
          </p>

          <p>
            The same God who walked with Joseph in the pit, the prison, and finally the palace is still
            writing stories of redemption today. He sees every tear, hears every prayer, and is working even
            when you cannot see it.
          </p>

          <p className="text-lg font-medium text-brand-navy">
            Your greatest setback may become the very platform for God's greatest purpose in your life.
          </p>

          <p>
            Because our story — and perhaps yours too — can be summed up in two unforgettable words:
          </p>

          <div className="text-center py-8">
            <div className="font-serif-display italic text-6xl md:text-7xl text-brand-gold-dark">But God.</div>
          </div>
        </div>
      </section>

      {/* 7) FINAL CTA — Deep Navy, large gold button */}
      <section className="relative overflow-hidden gradient-navy text-white">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 30% 30%, rgba(212,166,58,0.5), transparent 55%)',
          }}
        />
        <div className="relative max-w-[900px] mx-auto px-6 lg:px-10 py-24 md:py-32 text-center">
          <h3 className="font-serif-display text-4xl md:text-6xl leading-tight text-white">
            Every purchase creates opportunity.<br />
            <span className="italic text-brand-gold">Every gift carries hope.</span>
          </h3>
          <p className="mt-6 text-white/70 max-w-xl mx-auto">
            Wear something meaningful. Gift something eternal. Support a mission that turns pain into purpose.
          </p>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/shop"
              className="inline-flex items-center gap-3 bg-brand-gold hover:bg-brand-gold-2 text-brand-navy font-semibold px-9 py-5 rounded-full text-xs uppercase tracking-[0.3em] transition-colors duration-500 shadow-2xl shadow-brand-gold/30"
              data-testid="about-cta-shop"
            >
              Shop with Purpose
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="border border-white/40 hover:border-brand-gold text-white hover:text-brand-gold px-9 py-5 rounded-full text-xs uppercase tracking-[0.3em] transition-colors"
            >
              Share Your Story
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
